// ADR-0128 — Hub Execution Isolation. Types + pure helpers for the async
// operation-run API. The hub's `execute-preview` endpoint is now async: it
// returns `202 { runId }` immediately and the caller polls
// `GET /runs/:runId` until the run reaches a terminal status. Row/preview
// data is still read from the node cache AFTER the run finishes (the run
// response carries no rows).
//
// Everything here is framework-free so it can be unit-tested directly (the one
// import is `ApiRequestError`, a plain class). The React glue lives in
// `hooks/useOperationRun.ts`.

import { ApiRequestError } from "./api.js";

/** Overall run lifecycle status. */
export type RunStatus = "queued" | "running" | "succeeded" | "failed" | "cancelled";

/** Per-node status within a run. */
export type RunNodeStatus =
  | "waiting"
  | "running"
  | "from_cache"
  | "succeeded"
  | "failed"
  | "skipped"
  | "cancelled"; // ADR-0130: node interrupted by a run cancel

/** The `run` object returned by GET /runs/:runId (and the runs list). */
export type OperationRun = {
  id: string;
  tenant_id: string;
  operation_id: string;
  target_node_id: string;
  additional_target_node_ids: string[] | null;
  context: unknown;
  force_refresh: boolean;
  run_target: string | null;
  status: RunStatus;
  error: string | null;
  cancel_requested: boolean;
  /** Display name — NEVER a raw account UUID. May be null. */
  requested_by_name: string | null;
  heartbeat_at: string | null;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
  updated_at: string;
};

/** Per-node row returned alongside a run. */
export type OperationRunNode = {
  node_id: string;
  node_type: string;
  status: RunNodeStatus;
  row_count: number | null;
  /** This run's ACTUAL time on the node (for from_cache: the serve, not the original production). */
  duration_ms: number | null;
  /** from_cache provenance: when the served cache was originally produced ("data as of"). */
  cache_produced_at: string | null;
  error: string | null;
  execution_id: string | null;
  started_at: string | null;
  finished_at: string | null;
};

export type OperationRunDetail = {
  run: OperationRun;
  nodes: OperationRunNode[];
};

/** True once the run has reached a state that stops polling. */
export function isTerminalRunStatus(status: RunStatus): boolean {
  return status === "succeeded" || status === "failed" || status === "cancelled";
}

/** True while the run is still active (drives the mount-poll loop). */
export function isActiveRunStatus(status: RunStatus): boolean {
  return status === "queued" || status === "running";
}

/**
 * Canvas badge status for a node. This is the visual vocabulary the canvas
 * node renders. It is a superset of the run-node statuses plus the terminal
 * outcomes we persist as last-outcome badges.
 */
export type NodeBadgeStatus =
  | "idle"
  | "waiting"
  | "running"
  | "from_cache"
  | "success"
  | "error";

/**
 * Map a per-node run status to the canvas badge vocabulary. Pure — unit
 * tested. `skipped` nodes carry no badge (idle); `succeeded` → success,
 * `failed` → error, `from_cache` stays distinct so the canvas can show the
 * cache glyph instead of the running pulse.
 *
 * This is the status-only mapping and knows nothing about the run the node
 * belongs to. The canvas wants `nodeBadgeForRun` instead — a node's badge also
 * depends on whether its run is still going and whether the node is that run's
 * target.
 */
export function runNodeToBadgeStatus(status: RunNodeStatus): NodeBadgeStatus {
  switch (status) {
    case "waiting":
      return "waiting";
    case "running":
      return "running";
    case "from_cache":
      return "from_cache";
    case "succeeded":
      return "success";
    case "failed":
      return "error";
    case "skipped":
      return "idle";
    case "cancelled":
      // ADR-0130: a cancelled node carries no canvas decoration — the outcome
      // lives in run history / Trace, not as a stuck pulse.
      return "idle";
    default:
      return "idle";
  }
}

/**
 * Does this run materialize its TARGET node OUTSIDE the chain walk?
 *
 * `execute_persist` calls `executeNodeChain` with `skipTarget: true`: the merge
 * and the version write happen in the materialization shell, never as a chain
 * step. The hub therefore finalizes the persist node's chain row `skipped` on
 * EVERY persist run, successful ones included. For that node, `skipped` is a
 * statement about the chain, not about the load — so it must never be read as
 * "did not run because an ancestor failed". The load's fate is the RUN's fate.
 *
 * Defect, 2026-09-13: both the canvas and the run-history row read it the other
 * way and painted a by-design skip red, which sent a live diagnosis down a
 * wrong path.
 */
export function isOutOfChainTargetContext(runContext: unknown): boolean {
  return runContext === "execute_persist";
}

/** Is `nodeId` one of this run's targets (primary or additional)? */
export function isRunTargetNode(
  run: Pick<OperationRun, "target_node_id" | "additional_target_node_ids">,
  nodeId: string
): boolean {
  if (run.target_node_id === nodeId) return true;
  return (run.additional_target_node_ids ?? []).includes(nodeId);
}

/**
 * A run-history row for the persist node of an `execute_persist` run — the
 * out-of-chain target described above. An `execute_persist` run has exactly one
 * persist node in its graph walk (the node it targets), so the node type plus
 * the run context identify it without needing the target id on the wire.
 */
function isOutOfChainPersistRow(
  nodeStatus: string,
  opts: { runContext?: unknown; nodeType?: string } | undefined
): boolean {
  return (
    nodeStatus === "skipped" &&
    opts?.nodeType === "persist" &&
    isOutOfChainTargetContext(opts?.runContext)
  );
}

/**
 * The canvas badge for one node of one run — the single place the canvas decides
 * what a node looks like, so the pulse and the outcome can never disagree.
 *
 * Three rules beyond the plain per-node mapping:
 *
 *  1. While the run is ACTIVE its target(s) pulse `running` for the whole run.
 *     The node you clicked Run on executes LAST, so its own row reads `waiting`
 *     for most of the run; without this the pulse would sit on an ancestor.
 *  2. A TERMINAL run has no live nodes. The run row and the node rows settle in
 *     two separate writes and on the failure path the RUN row goes terminal
 *     first — so a poll landing in that window sees `running`/`waiting` node
 *     rows under a finished run. Letting that reach the canvas is the stuck
 *     pulse (a node still pulsing long after the run failed).
 *  3. The out-of-chain persist target reads its outcome from the run, never
 *     from its always-`skipped` chain row.
 */
export function nodeBadgeForRun(
  run: Pick<OperationRun, "status" | "context" | "target_node_id" | "additional_target_node_ids">,
  node: Pick<OperationRunNode, "node_id" | "status">
): NodeBadgeStatus {
  const isTarget = isRunTargetNode(run, node.node_id);

  if (isActiveRunStatus(run.status)) {
    return isTarget ? "running" : runNodeToBadgeStatus(node.status);
  }

  // Rule 2 — a node row that has not settled yet cannot mean "still going"
  // under a terminal run.
  const unsettled = node.status === "running" || node.status === "waiting";
  const didNotExecute = unsettled || node.status === "skipped";

  // Rule 3 — the persist target was materialized outside the chain.
  if (isTarget && didNotExecute && isOutOfChainTargetContext(run.context)) {
    if (run.status === "succeeded") return "success";
    if (run.status === "failed") return "error";
    return "idle"; // cancelled — no canvas decoration (ADR-0130)
  }

  // A failed run whose target never executed leaves NO mark on the node the
  // user ran unless we stamp one — mirrors run history's upstream attribution.
  if (isTarget && didNotExecute && run.status === "failed") return "error";

  if (unsettled) return "idle";
  return runNodeToBadgeStatus(node.status);
}

/**
 * ADR-0130: label + pill class for a node's run-history row. When the node
 * EXECUTED (node status ∈ succeeded/failed/cancelled) the row shows the node's
 * own outcome. When it did NOT execute but the run it TARGETED terminated
 * failed/cancelled, it's an instigated upstream failure → "Failed · upstream" /
 * "Cancelled · upstream" (the Trace pinpoints the ancestor). Pure — unit tested.
 *
 * `opts` carries the run context + this node's type so the out-of-chain persist
 * target (see `isOutOfChainTargetContext`) reports the LOAD's outcome instead of
 * an upstream attribution it cannot support. Omitting `opts` keeps the ADR-0130
 * behaviour for every other caller.
 */
export function historyRowStatus(
  nodeStatus: string,
  runStatus: string | null | undefined,
  opts?: { runContext?: unknown; nodeType?: string }
): { label: string; cls: string } {
  if (isOutOfChainPersistRow(nodeStatus, opts)) {
    switch (runStatus) {
      case "failed":
        // The load failed. WHERE it failed — the upstream pull or the merge —
        // is the Trace's job; "· upstream" would assert an attribution this row
        // cannot support, and is wrong outright when the merge is what died.
        return { label: "Failed", cls: "pill pillBad" };
      case "cancelled":
        return { label: "Cancelled", cls: "pill" };
      case "succeeded":
        return { label: "Executed", cls: "pill pillOk" };
      default:
        return { label: "Running", cls: "pill pillWarn" };
    }
  }
  const executed = nodeStatus === "succeeded" || nodeStatus === "success" || nodeStatus === "failed" || nodeStatus === "cancelled";
  if (!executed && (runStatus === "failed" || runStatus === "cancelled")) {
    return {
      label: runStatus === "cancelled" ? "Cancelled · upstream" : "Failed · upstream",
      cls: "pill pillBad",
    };
  }
  switch (nodeStatus) {
    case "failed":
      return { label: "Failed", cls: "pill pillBad" };
    case "from_cache":
      return { label: "Served from cache", cls: "pill pillWarn" };
    case "skipped":
      return { label: "Skipped", cls: "pill" };
    case "cancelled":
      return { label: "Cancelled", cls: "pill" };
    case "succeeded":
    case "success":
      return { label: "Executed", cls: "pill pillOk" };
    default:
      return { label: nodeStatus, cls: "pill" };
  }
}

/**
 * What a run-history row says about its outcome, from THIS node's own error and
 * the run's. A step that succeeded inside a run that failed at a sibling is a
 * success: its row carries no error, only a labelled run-level note, so the
 * run's failure text is never read as the step's (bugfix 2026-10-29). Pure.
 *
 * - the node failed → its own error (the run's text as a fallback, since a run
 *   that died with a running node copies the reason onto both);
 * - the node never executed (an instigated run that died upstream, the
 *   "Failed · upstream" row) → the run's error IS this row's story;
 * - the node executed and the run failed anyway → no error; a note naming the
 *   step the run failed at (`failedStep`: null when no other step failed —
 *   the run died outside any step; `label` null when that step is no longer
 *   on the canvas).
 */
export function historyRowOutcome(args: {
  nodeStatus: string;
  runStatus: string | null | undefined;
  nodeError: string | null | undefined;
  runError: string | null | undefined;
  failedStep: { label: string | null } | null;
}): { error: string | null; runNote: string | null } {
  const nodeError = args.nodeError ?? null;
  const runError = args.runError ?? null;
  if (args.nodeStatus === "failed") return { error: nodeError ?? runError, runNote: null };
  const executed =
    args.nodeStatus === "succeeded" || args.nodeStatus === "success" || args.nodeStatus === "cancelled";
  if (!executed) return { error: nodeError ?? runError, runNote: null };
  if (args.runStatus !== "failed") return { error: nodeError, runNote: null };
  const label = args.failedStep?.label?.trim();
  return {
    error: nodeError,
    runNote: label ? `Run failed at ${label}` : args.failedStep ? "Run failed at another step" : "Run failed outside this step",
  };
}

/**
 * ADR-0130: the upstream closure of a node — the node itself ∪ all its data-edge
 * ancestors. Pure (graph-only). Used to scope a Trace opened from a node's
 * History to that node and its lineage (a trace never shows nodes downstream of
 * its subject). `dataEdges` must already be filtered to data edges.
 */
export function upstreamClosureIds(
  dataEdges: ReadonlyArray<{ source: string; target: string }>,
  startNodeId: string
): Set<string> {
  const reverseAdj = new Map<string, string[]>();
  for (const e of dataEdges) {
    const list = reverseAdj.get(e.target) ?? [];
    list.push(e.source);
    reverseAdj.set(e.target, list);
  }
  const closure = new Set<string>();
  const visit = (id: string) => {
    if (closure.has(id)) return;
    closure.add(id);
    for (const up of reverseAdj.get(id) ?? []) visit(up);
  };
  visit(startNodeId);
  return closure;
}

/**
 * ADR-0130: human label for an operation-run `context`. Never render the raw
 * token. Unknown/legacy contexts fall back to a readable title-case of the token.
 */
export function runContextLabel(context: unknown): string {
  switch (context) {
    case "execute_preview":
      return "Preview run";
    case "execute_persist":
      return "Persist";
    case "page_refresh":
      return "Page refresh";
    case "page_heal":
      return "Page heal";
    case "schedule":
      return "Schedule";
    case "subscription":
      return "Subscription";
    case "model_train":
      return "Model training";
    case "ai_context":
      return "AI assistant";
    case "optimize_solve":
      return "Optimize solve";
    default:
      return typeof context === "string" && context
        ? context.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
        : "Run";
  }
}

/**
 * Did the REQUEST die, rather than the run?
 *
 * A persist load holds its HTTP request open at interactive priority for the
 * whole materialization, and the hub's own `get_run` contract is that "a run
 * keeps going server-side after the call that started it returns." So a browser
 * that gives up — a dropped socket, a gateway timeout, a sleeping laptop — says
 * nothing at all about the load, which is very likely still running.
 *
 * `apiFetch` throws `ApiRequestError` only when the hub ANSWERED (any non-2xx).
 * Anything else out of `fetch` is the transport: a bare `TypeError: Failed to
 * fetch`, a `DOMException` on abort. Defect, 2026-09-13: a dropped fetch was
 * reported as `Execution failed: Failed to fetch` while the run was alive and
 * heartbeating, and went on for another six minutes after the toast.
 */
export function isTransportFailure(err: unknown): boolean {
  return !(err instanceof ApiRequestError);
}

/**
 * Given the current per-node badge map and a run's node list, compute the
 * next badge map. Nodes not present in the run keep their existing badge
 * (last-outcome). Used by both live-run updates and mount reconciliation.
 */
export function applyRunNodesToBadges(
  current: Readonly<Record<string, NodeBadgeStatus>>,
  nodes: readonly OperationRunNode[]
): Record<string, NodeBadgeStatus> {
  const next: Record<string, NodeBadgeStatus> = { ...current };
  for (const n of nodes) {
    next[n.node_id] = runNodeToBadgeStatus(n.status);
  }
  return next;
}
